import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import { useAuth } from 'react-oidc-context';
import { useParams, Link as RouterLink } from 'react-router-dom';
import cloudApi from '../../app/api/cloudApi';
import SuspenseDiv from '../../layouts/SuspenseDiv';
import FaceScannerDevicesPanel from './sections/face-scanner-devices-panel';
import FaceScannerGatesPanel from './sections/face-scanner-gates-panel';

export default function FaceScannerPage() {
  const { siteId } = useParams();
  const auth = useAuth();

  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [site, setSite] = useState(null);

  useEffect(() => {
    const initializePage = async () => {
      setError(null);
      setPageLoading(true);

      if (!auth.isAuthenticated || !auth.user) {
        setError('Please log in to access this page');
        setPageLoading(false);
        return;
      }

      if (!siteId) {
        setError('Site ID is required');
        setPageLoading(false);
        return;
      }

      try {
        const siteResponse = await cloudApi.getSite(auth.user.access_token, siteId);
        if (siteResponse.data) {
          setSite(siteResponse.data);
        } else {
          setError('Site not found');
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load page');
      } finally {
        setPageLoading(false);
      }
    };

    initializePage();
  }, [siteId, auth.isAuthenticated, auth.user]);

  if (pageLoading) {
    return <SuspenseDiv />;
  }

  if (error) {
    return (
      <Container maxWidth={false}>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Face Scanner Devices & Gates | {site?.name || 'Site'}</title>
      </Helmet>

      <Container maxWidth={false}>
        <Stack mb={5}>
          <Typography variant="h4" gutterBottom>
            Face Scanner Devices & Gates
          </Typography>
          <Breadcrumbs aria-label="breadcrumb">
            <Link component={RouterLink} underline="hover" color="inherit" to="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/sites">
              Sites
            </Link>
            {site && <Typography color="text.secondary">{site.name}</Typography>}
            <Typography color="text.primary">Face Scanner Devices & Gates</Typography>
          </Breadcrumbs>
        </Stack>

        {site && (
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Site Information" />
            <CardContent>
              <Typography variant="h6">{site.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {site.url}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Box sx={{ mb: 5 }}>
          <FaceScannerDevicesPanel site={site} />
        </Box>

        <Divider sx={{ mb: 5 }} />

        <Box>
          <FaceScannerGatesPanel site={site} />
        </Box>
      </Container>
    </>
  );
}
